/**
 * Pure resize geometry (Vivarium styleguide §21). Framework-free so it can be
 * unit-tested and reused by both render paths. Returns the new {x,y,w,h} plus
 * which snap targets were hit (for drawing alignment guides).
 *
 * Handles: corners tl/tr/bl/br + edges t/b/l/r. Modifiers: ratio (Shift, keep
 * aspect), center (Alt, mirror the opposite edge). MIN clamp re-anchors to the
 * fixed edge so a frame can't invert. Edge-snap pulls an active edge to a
 * nearby target within SNAP px.
 */
export interface Rect { x: number; y: number; w: number; h: number }
export type ResizeSide = 'tl' | 'tr' | 'bl' | 'br' | 't' | 'b' | 'l' | 'r';
export interface ResizeMods { ratio?: boolean; center?: boolean }
export interface SnapTarget { axis: 'x' | 'y'; v: number }
export interface ResizeResult extends Rect { snapped: SnapTarget[] }

export const MIN_SIZE = 40;
const SNAP = 8;

export function computeResize(
  start: Rect,
  side: ResizeSide,
  dx: number,
  dy: number,
  mods: ResizeMods,
  snapTargets: SnapTarget[],
): ResizeResult {
  let l = start.x, r = start.x + start.w, t = start.y, b = start.y + start.h;
  const aL = side.includes('l'), aR = side.includes('r');
  const aT = side.includes('t'), aB = side.includes('b');

  if (aL) l += dx;
  if (aR) r += dx;
  if (aT) t += dy;
  if (aB) b += dy;

  // Alt: mirror the opposite edge so the box grows from its center.
  if (mods.center) {
    if (aL) r -= dx;
    if (aR) l -= dx;
    if (aT) b -= dy;
    if (aB) t -= dy;
  }

  // Edge-snap: pull an active edge onto a nearby target.
  const snapped: SnapTarget[] = [];
  for (const s of snapTargets) {
    if (s.axis === 'x') {
      if (aL && Math.abs(l - s.v) <= SNAP) { l = s.v; snapped.push(s); }
      if (aR && Math.abs(r - s.v) <= SNAP) { r = s.v; snapped.push(s); }
    } else {
      if (aT && Math.abs(t - s.v) <= SNAP) { t = s.v; snapped.push(s); }
      if (aB && Math.abs(b - s.v) <= SNAP) { b = s.v; snapped.push(s); }
    }
  }

  // Shift: keep aspect ratio (drive by the axis that changed more).
  if (mods.ratio && start.h !== 0) {
    const ratio = start.w / start.h;
    let w = r - l, h = b - t;
    if (Math.abs(w - start.w) >= Math.abs(h - start.h)) h = w / ratio;
    else w = h * ratio;
    if (aR) r = l + w; else if (aL) l = r - w;
    if (aB) b = t + h; else if (aT) t = b - h;
  }

  // MIN clamp, re-anchored to the fixed edge.
  if (r - l < MIN_SIZE) { if (aL) l = r - MIN_SIZE; else r = l + MIN_SIZE; }
  if (b - t < MIN_SIZE) { if (aT) t = b - MIN_SIZE; else b = t + MIN_SIZE; }

  return {
    x: Math.round(l),
    y: Math.round(t),
    w: Math.round(r - l),
    h: Math.round(b - t),
    snapped,
  };
}
